/**
 * KISSED University · College landing pages.
 * Route: /university/college/:collegeKey  (trichology | herbal | hhfw)
 *
 * Filters /api/kuhas/programs to show only the programs belonging to
 * this college's division. Each key resolves to a hard-coded set of
 * kuhas_programs slugs (COLLEGE_CONFIG.programSlugs) — this is the same
 * grouping surfaced elsewhere in the app; a future data-driven refactor
 * (Part 8) will move this mapping into the DB record itself.
 *
 * DRAFT COPY — 2026-07-06. Placeholder-quality mission/summary content
 * pending final review by Dr. Christy Vonta / admissions leadership.
 */
import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Sparkles, Leaf, FlaskConical, GraduationCap, ChevronLeft, ChevronRight, Clock, Award } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import UniversityTopNav from "@/components/UniversityTopNav";

const API = process.env.REACT_APP_BACKEND_URL;

// Slug → short public URL key (used to build /university/program/:key links).
// Mirrors URL_KEY_TO_KUHAS_SLUG in UniversityProgramDetail.jsx (inverse).
const SLUG_TO_URL_KEY = {
  level_1_scalp_scientist: "l1",
  level_2_associate_tri:   "l2",
  level_3_holistic_practitioner: "l3",
  dts: "dts",
  herbal_foundations:    "herbal-foundations",
  herbal_practitioner:   "herbal-practitioner",
  holistic_functional_wellness: "hhfw",
  cct: "cct",
};

// College config. Each entry declares which kuhas_programs slugs belong to
// that college and the placeholder-quality marketing copy for the landing.
const COLLEGE_CONFIG = {
  trichology: {
    key: "trichology",
    name: "College of Trichology Science",
    icon: Sparkles,
    tagline: "Where clinical rigor meets sacred work.",
    mission:
      "The College of Trichology Science trains the next generation of trichologists, scalp scientists, and doctoral-level practitioners through a fully articulated pathway — from foundational certification through advanced clinical residency and doctoral scholarship. Our curriculum unites the sciences of hair biology, scalp health, and functional wellness with the ethical, consent-first client care that defines the KISSED practice model.",
    highlights: [
      "Four articulated levels: Certified Scalp Scientist → Associate Trichologist → Holistic Trichology Practitioner → Doctoral Trichology Scholar",
      "Level II unlocks referral initiation for supervised practice",
      "Level III and DTS unlock protocol authorship privileges",
      "Standalone Clinically Certified Trichologist (CCT) residency available as an external pathway for practitioners with prior training",
    ],
    programSlugs: [
      "level_1_scalp_scientist",
      "level_2_associate_tri",
      "level_3_holistic_practitioner",
      "dts",
      "cct",
    ],
    ctaLabel: "Take the Pathway Quiz",
    ctaHref: "/university/quiz",
    secondaryCta: { label: "Open Enrollment", href: "/university/enroll" },
  },
  herbal: {
    key: "herbal",
    name: "College of Herbal Medicine",
    icon: Leaf,
    tagline: "Plant medicine in service of clinical care.",
    mission:
      "The College of Herbal Medicine at KISSED University offers a two-tier pathway in western and folk herbalism, formulated for practitioners who want to integrate botanical medicine into holistic client care. Foundations builds the working knowledge — plant identification, materia medica, safety, and preparation — while the Practitioner tier authorizes protocol authorship and formal client-facing herbal recommendation.",
    highlights: [
      "Two articulated tiers: Herbal Medicine Foundations → Herbal Medicine Practitioner",
      "Practitioner tier requires Foundations completion — enforced server-side",
      "Foundations opens direct enrollment; Practitioner completion unlocks protocol authorship",
      "Curriculum grounded in trichology-adjacent applications — scalp, hair, gut, hormones, adaptogenic support",
    ],
    programSlugs: ["herbal_foundations", "herbal_practitioner"],
    ctaLabel: "Enroll in Foundations",
    ctaHref: "/university/enroll",
    secondaryCta: { label: "Join the Herbal Waitlist", href: "/university/herbal-waitlist" },
  },
  hhfw: {
    key: "hhfw",
    name: "College of Holistic Health & Functional Wellness",
    icon: FlaskConical,
    tagline: "Root-cause practice for the whole person.",
    mission:
      "The College of Holistic Health & Functional Wellness trains practitioners in a root-cause, systems-based approach to whole-person health. Coursework spans the gut-hair connection, endocrine and adrenal patterns, sleep and stress physiology, hormonal transitions, and the functional assessment tools that let a practitioner author defensible, individualized protocols. Graduates earn the Holistic Health & Functional Wellness Practitioner credential.",
    highlights: [
      "20-course practitioner curriculum spanning digestive, endocrine, and neuro-immune systems",
      "Emphasis on functional lab interpretation, client history, and root-cause reasoning",
      "Completion unlocks protocol authorship for HHFW-scoped client work",
      "Open enrollment — no application required",
    ],
    programSlugs: ["holistic_functional_wellness"],
    ctaLabel: "Enroll Now",
    ctaHref: "/university/enroll",
    secondaryCta: { label: "Take the Pathway Quiz", href: "/university/quiz" },
  },
};

function programUrl(slug) {
  const key = SLUG_TO_URL_KEY[slug];
  return key ? `/university/program/${key}` : "/university";
}

function formatTuition(p) {
  if (p.billing_model === "per_credit" && p.per_credit_rate) {
    return `$${p.per_credit_rate}/credit hour`;
  }
  if (p.pay_in_full_price) {
    return `$${Number(p.pay_in_full_price).toLocaleString()} (pay in full)`;
  }
  return "See program detail";
}

export default function UniversityCollege() {
  const { collegeKey } = useParams();
  const config = COLLEGE_CONFIG[String(collegeKey || "").toLowerCase()];

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    setLoading(true);
    fetch(`${API}/api/kuhas/programs`)
      .then((r) => r.json())
      .then((body) => {
        const all = body.items || [];
        const filtered = all.filter((p) => config.programSlugs.includes(p.slug));
        // Preserve seed order (from backend .sort("order", 1))
        setPrograms(filtered);
      })
      .catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, [config]);

  if (!config) {
    return <Navigate to="/university" replace />;
  }

  const Icon = config.icon;

  return (
    <div data-testid={`university-college-${config.key}`} className="min-h-screen bg-gradient-to-b from-[#faf7f5] via-white to-[#f5f0ec]">
      <UniversityTopNav />

      <main className="max-w-6xl mx-auto px-6 py-16">
        <Link to="/university" className="text-xs text-slate-600 inline-flex items-center hover:text-purple-900" data-testid="college-back-link">
          <ChevronLeft className="w-3 h-3 mr-1" /> Back to KISSED University
        </Link>

        {/* Draft-copy notice */}
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] text-amber-900" data-testid="draft-copy-flag">
          <Clock className="w-3 h-3" />
          Draft copy — pending final review
        </div>

        {/* Hero */}
        <div className="mt-6 flex items-start gap-6">
          <div className="rounded-2xl bg-purple-50 p-4 shrink-0 hidden sm:block">
            <Icon className="w-10 h-10 text-purple-900" />
          </div>
          <div className="flex-1">
            <span className="text-xs uppercase tracking-[0.18em] text-purple-900 font-semibold">KISSED University</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight" data-testid="college-title">
              {config.name}
            </h1>
            <p className="mt-4 text-lg text-slate-700 italic">{config.tagline}</p>
          </div>
        </div>

        {/* Mission */}
        <section className="mt-10 max-w-3xl">
          <h2 className="font-display text-2xl text-slate-900">Our Mission</h2>
          <p className="mt-3 text-base text-slate-700 leading-relaxed" data-testid="college-mission">
            {config.mission}
          </p>
        </section>

        {/* Highlights */}
        <section className="mt-10 rounded-2xl border border-purple-100 bg-purple-50/40 p-6 sm:p-8" data-testid="college-highlights">
          <h2 className="font-display text-2xl text-slate-900">What Sets This College Apart</h2>
          <ul className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed">
            {config.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-3">
                <Award className="w-4 h-4 text-purple-900 mt-0.5 shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Programs */}
        <section className="mt-12" data-testid="college-programs">
          <h2 className="font-display text-2xl text-slate-900">Programs of Study</h2>
          <p className="mt-2 text-sm text-slate-600">
            {config.programSlugs.length === 1 ? "One program" : `${config.programSlugs.length} programs`} offered by the {config.name}.
          </p>

          {loading ? (
            <div className="mt-6 text-sm text-slate-500">Loading programs…</div>
          ) : programs.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
              No active programs published for this college yet.
            </div>
          ) : (
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {programs.map((p) => (
                <Link
                  key={p.slug}
                  to={programUrl(p.slug)}
                  data-testid={`college-program-card-${p.slug}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-purple-300 hover:shadow-md transition group"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="font-mono text-[10px]">{p.level_label}</Badge>
                    {p.unlocks_protocol_authorship && (
                      <Badge className="bg-emerald-700 text-white text-[10px]">Protocol Authorship</Badge>
                    )}
                    {p.unlocks_referral_initiation && (
                      <Badge className="bg-indigo-700 text-white text-[10px]">Referral Initiation</Badge>
                    )}
                  </div>
                  <div className="font-display text-lg text-slate-900 group-hover:text-purple-900">
                    {p.title}
                  </div>
                  <div className="mt-3 text-xs text-slate-600 space-y-1">
                    {p.num_courses ? <div>{p.num_courses} {p.num_courses === 1 ? "course" : "courses"}</div> : null}
                    {p.total_clock_hours ? <div>{p.total_clock_hours} clock hours</div> : null}
                    <div className="font-medium text-purple-900">{formatTuition(p)}</div>
                    {p.requires_application && (
                      <div className="text-amber-800 text-[11px] mt-1">Formal application required</div>
                    )}
                  </div>
                  <div className="mt-4 text-xs text-purple-900 font-medium inline-flex items-center">
                    View program detail <ChevronRight className="w-3 h-3 ml-1" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="mt-14 rounded-2xl border border-purple-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="college-cta">
          <div>
            <h3 className="font-display text-xl text-slate-900">Ready to begin?</h3>
            <p className="mt-1 text-sm text-slate-600">Choose your path — we&apos;ll match you to the right program.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={config.ctaHref}>
              <Button size="lg" className="bg-purple-900 hover:bg-purple-800 text-white px-6" data-testid={`college-cta-primary-${config.key}`}>
                {config.ctaLabel} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            {config.secondaryCta && (
              <Link to={config.secondaryCta.href}>
                <Button size="lg" variant="outline" className="border-purple-900 text-purple-900 hover:bg-purple-50 px-6" data-testid={`college-cta-secondary-${config.key}`}>
                  {config.secondaryCta.label}
                </Button>
              </Link>
            )}
          </div>
        </section>

        {/* Footer link */}
        <div className="mt-10 text-center text-xs text-slate-500">
          <Link to="/university" className="hover:text-purple-900 inline-flex items-center">
            <GraduationCap className="w-3 h-3 mr-1" /> All KISSED University Colleges
          </Link>
        </div>
      </main>
    </div>
  );
}
